/**
 * demoShared.tsx — queue #646 §UAT-2026-07-07-R646 §R646-4.
 *
 * 10종 신규 데모 패널(StateMachine/Saga/Report/RetentionPreview/ConfigLookup/Barcode/
 * TemplateRender/Pdf/ImageResize/FeatureToggle)이 공유하는 스타일 상수 + 소형 헬퍼.
 * 기존 KafkaDemoPanel/RabbitDemoPanel 의 카드/버튼/오류박스 톤을 그대로 재사용해
 * 10개 파일에 동일 스타일 객체를 중복 정의하지 않는다(재구현 금지 원칙 · CLAUDE.md §12
 * 정신을 FE 컴포넌트 레벨에도 적용). CSS var 만 사용 — 하드코딩 색상 0.
 */
import { ErrorState } from '@ui-kit';
import type { ApiError } from '../../types/showcase';

export const cardStyle: React.CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

export const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-2)',
  marginTop: 0,
};

export const subtitleStyle: React.CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
  marginBottom: 'var(--spacing-6)',
};

export const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: 'var(--spacing-1)',
};

export const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
};

export const monoStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-sm)',
};

export const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  fontFamily: 'var(--font-family-sans)',
  cursor: 'pointer',
};

export const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

export const resultBoxStyle: React.CSSProperties = {
  ...cardStyle,
  background: 'var(--color-surface-elevated)',
};

/** 안전 배너(격리/미리보기/읽기전용 고지) — C067/C027/C080 상단(§R646-4). */
export function DemoSafetyBanner({ text }: { text: string }) {
  return (
    <p
      role="note"
      style={{
        margin: '0 0 var(--spacing-4) 0',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-secondary)',
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--spacing-2) var(--spacing-3)',
      }}
    >
      {text}
    </p>
  );
}

/**
 * 공통 오류 상태 — ApiError 정규화 재사용(ui-kit ErrorState).
 * 데모는 오류 계약(ErrorResponse) 자체를 학습 대상으로 보여주므로 error.code 를 앞에 노출한다
 * (예: `[STATE-409-001] ...`). code 가 없으면 message 만 표시.
 */
export function DemoErrorState({ title, error }: { title: string; error: ApiError | null }) {
  if (!error) return null;
  const codePrefix = error.code ? `[${error.code}] ` : '';
  return (
    <ErrorState
      title={title}
      message={`${codePrefix}${error.message}${error.traceId ? ` (traceId: ${error.traceId})` : ''}`}
    />
  );
}

/** 빈 상태(호출 전) 안내 문구. */
export function DemoEmptyHint({ text }: { text: string }) {
  return (
    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
      {text}
    </p>
  );
}
